/*
 * FILE: unified_ui/src/lib/abandonBeacon.ts
 * PURPOSE: Fire a one-shot order.abandoned beacon when the app is closed/hidden
 *          while a wizard run is in_progress (NOT on navigation/route change).
 * OWNS: pagehide + visibilitychange→hidden listeners, per-run once guard.
 * EXPORTS: initAbandonBeacon.
 *
 * Mirrors the presence-off beacon pattern (chat.ts): sendBeacon survives page
 * unload. Reads ONLY the wizard store — no router hooks, no beforeunload-based
 * navigation detection — so SPA route changes can never trigger it.
 */
import { useWizardStore } from '@/stores/wizard'

/** runId already reported as abandoned — prevents double beacons per run. */
let reportedRunId: string | null = null

/**
 * A run counts as "in_progress" only while it is genuinely mid-flight:
 * runId set, wizard on the running step, and no error (a failed run is not
 * abandoned — it failed). Completed runs sit on the 'done' step; cancelled
 * runs clear runId entirely.
 */
function isRunInProgress(): boolean {
  const w = useWizardStore()
  if (!w.runId) return false
  if (w.step !== 'running') return false
  if (w.error) return false
  return true
}

function maybeSendAbandonBeacon(): void {
  try {
    const w = useWizardStore()
    if (!isRunInProgress()) return
    if (reportedRunId === w.runId) return
    reportedRunId = w.runId
    const payload = JSON.stringify({
      event: 'order.abandoned',
      text: 'Заказ брошен',
      data: { runId: w.runId, stage: w.step },
    })
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/report', new Blob([payload], { type: 'application/json' }))
    }
  } catch {
    // fire-and-forget — never break unload
  }
}

let registered = false

/** Register the app-close/hide listeners once. Call from App.vue on mount. */
export function initAbandonBeacon(): void {
  if (registered) return
  registered = true
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  window.addEventListener('pagehide', maybeSendAbandonBeacon)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') maybeSendAbandonBeacon()
  })
}
